import React, { useState, forwardRef, useCallback, useEffect } from 'react';
import { Box, Stack, Typography, Button, CircularProgress, Alert, Autocomplete } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { PinnacleTextField } from '../../../components/PinnacleTextField';
import { Term } from '../../../api/interfaces/Terms';
import { getTerms } from '../../../api/services/terms';
import { handleApiError } from '../../../utils/errorHandler';
import { pushNotification } from '../../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider';
import { NotificationTypes } from '../../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider.types';

interface TransferUsersTermUsersDrawerContentProps {
  isSubmitting: boolean;
  selectedFile: File | null;
  onFileChange: (file: File | null) => void;
  onSubmit: (file: File) => void;
  termId: string;
  setTermId: React.Dispatch<React.SetStateAction<string>>;
}

const TransferUsersTermUsersDrawerContent = forwardRef<HTMLInputElement, TransferUsersTermUsersDrawerContentProps>(
  ({ isSubmitting, selectedFile, onFileChange, onSubmit, termId, setTermId }, fileInputRef) => {
    const [terms, setTerms] = useState<Term[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const fetchTerms = useCallback(async () => {
      try {
        const termsResponse = await getTerms({ status: 'active' });
        setTerms(termsResponse.data);
      } catch (error) {
        handleApiError(error, {
          notify: (msg) => {
            pushNotification({
              details: msg,
              type: NotificationTypes.Error,
            });
          },
        });
      }
    }, []);


    // Initial state when component load
    useEffect(() => {
      const run = async () => {
        try {
          setIsLoading(true);
          await fetchTerms();
        } catch (error) {
          handleApiError(error, {
            notify: (msg) => {
              pushNotification({
                details: msg,
                type: NotificationTypes.Error,
              });
            },
          });
        } finally {
          setIsLoading(false);
        }
      };
      run();
    }, [fetchTerms]);

    const handleUploadClick = () => {
      (fileInputRef as React.RefObject<HTMLInputElement>).current?.click();
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0] || null;
      onFileChange(file);
    };

    const handleSubmit = () => {
      if (selectedFile) {
        onSubmit(selectedFile);
      }
    };

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ pb: 2 }}>
          <Alert severity="info" icon={false} sx={{ backgroundColor: '#E5F8FE', color: '#26C6F9' }}>
            <Typography variant="subtitle2">Bulk upload allows you to transfer multiple users to a different term in one action.</Typography>
          </Alert>
        </Box>

        <Autocomplete
          options={terms}
          getOptionLabel={(option: Term) => option.name}
          value={terms.find((term) => term._id === termId) || undefined}
          onChange={(_, newValue: Term | null) => setTermId(newValue?._id ?? '')}
          disabled={(isSubmitting || isLoading)}
          loading={isLoading}
          disableClearable
          renderInput={(params) => (
            <PinnacleTextField
              {...params}
              label="Term"
              fullWidth
              slotProps={{
                input: {
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoading ? <CircularProgress size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }
              }}
            />
          )}
        />

        {/* Upload Box */}
        <Stack spacing={2}>
          <Box
            onClick={handleUploadClick}
            sx={{
              border: '2px dashed #ccc',
              borderRadius: '8px',
              p: 2,
              cursor: 'pointer',
              '&:hover': { backgroundColor: '#f9f9f9' },
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              {selectedFile ? (
                <>
                  <CloudUploadIcon sx={{ fontSize: 40, color: '#A8A8A8', height: '50px', width: '50px' }} />
                  <Box>
                    <Typography
                      variant="subtitle1"
                      fontWeight={500}
                      noWrap
                      sx={{
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        maxWidth: 200
                      }}
                    >
                      {selectedFile.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Click to change file
                    </Typography>
                  </Box>
                </>
              ) : (
                <>
                  <CloudUploadIcon sx={{ fontSize: 40, color: '#A8A8A8', height: '50px', width: '50px' }} />
                  <Box>
                    <Typography variant="subtitle1" fontWeight={500}>
                      Upload XLSX File
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Browse file to upload
                    </Typography>
                  </Box>
                </>
              )}
            </Stack>
            <input
              type="file"
              accept=".xlsx"
              ref={fileInputRef}
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
          </Box>
        </Stack>

        {/* Submit Button */}
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={isSubmitting || !selectedFile || !termId}
          startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
        >
          TRANSFER USERS
        </Button>
      </Box>
    );
  }
);

export default TransferUsersTermUsersDrawerContent;
